import { Link, useParams } from "react-router-dom";
import { quizRegistry } from "@quizzes/_registry";
import type { Question } from "@quizzes/_schema";
import { isRandomizedQuestion, questionLabel } from "../utils/questions";
import { RandomizedChip } from "../components/RandomizedChip";
import { ResponsesExport } from "../components/ResponsesExport";

function describeQuestion(q: Question): string {
  switch (q.type) {
    case "single":
      return `Single-choice (${q.options.length} options)`;
    case "multi":
      return `Multi-select (${q.options.length} options)`;
    case "slider":
      return `Slider ${q.min}–${q.max}`;
    case "text":
      return "Free text";
    case "date":
      return "Date";
    case "number":
      return "Number";
    case "ruler":
      return "Ruler";
    case "wheel":
      return "Scroll wheel";
    case "info":
      return "Info screen";
    case "summary":
    case "signal-summary":
      return "Summary screen";
    case "editorial":
      return "Editorial screen";
    case "story":
      return "Story screen";
  }
}

export function QuizDetail() {
  const { slug = "" } = useParams();
  const quiz = quizRegistry[slug];

  if (!quiz) {
    return (
      <div>
        <p className="text-mars-white/60">
          Unknown quiz{" "}
          <code className="mono rounded bg-mars-charcoal px-1.5 py-0.5">{slug}</code>.{" "}
          <Link to="/quizzes" className="text-mars-orange underline">
            Back to list
          </Link>
        </p>
      </div>
    );
  }

  return (
    <div>
      <Link
        to="/quizzes"
        className="mono text-[0.7rem] uppercase tracking-[0.16em] text-mars-white/50 transition-colors hover:text-mars-white"
      >
        &larr; All quizzes
      </Link>
      <header className="mb-8 mt-3 flex flex-wrap items-start justify-between gap-4">
        <div>
          <span className="eyebrow">{quiz.slug}</span>
          <h1 className="heading mt-1 text-3xl">{quiz.meta.title}</h1>
          <p className="mt-2 text-sm text-mars-white/60">
            {quiz.questions.length} questions ·{" "}
            <span className="mono uppercase tracking-[0.12em] text-mars-white/50">
              capture: {quiz.capture?.fields.join(", ") || "none"}
            </span>
          </p>
        </div>
        <div className="flex gap-3">
          <Link
            to={`/quizzes/${quiz.slug}/preview`}
            className="mono rounded-xl bg-mars-orange px-5 py-2.5 text-[0.7rem] font-bold uppercase tracking-[0.18em] text-mars-black transition-colors hover:bg-mars-orange-deep"
          >
            Preview
          </Link>
          <Link
            to={`/analytics?slug=${quiz.slug}`}
            className="mono rounded-xl border border-mars-line px-5 py-2.5 text-[0.7rem] font-bold uppercase tracking-[0.18em] text-mars-white transition-colors hover:border-mars-orange hover:text-mars-orange"
          >
            Analytics
          </Link>
        </div>
      </header>

      <ResponsesExport quiz={quiz} />

      <section className="mb-8 rounded-2xl border border-mars-line bg-mars-charcoal/40 p-6">
        <span className="eyebrow">Intro</span>
        <p className="mt-2 text-base text-mars-white">{quiz.meta.introHeadline}</p>
        {quiz.meta.introBody ? (
          <p className="mt-2 text-sm text-mars-white/70">{quiz.meta.introBody}</p>
        ) : null}
      </section>

      <section>
        <div className="mb-3 flex items-baseline justify-between">
          <span className="eyebrow">Questions</span>
          <span className="mono text-[0.7rem] uppercase tracking-[0.16em] text-mars-white/40">
            {quiz.questions.length} steps
          </span>
        </div>
        <ol className="space-y-2">
          {quiz.questions.map((q, idx) => (
            <li
              key={q.id}
              className="rounded-xl border border-mars-line bg-mars-night px-5 py-4 transition-colors hover:bg-mars-charcoal/50"
            >
              <div className="flex items-baseline gap-3">
                <span className="mono text-[0.65rem] uppercase tracking-[0.18em] text-mars-white/40">
                  {String(idx + 1).padStart(2, "0")}
                </span>
                <span className="mono text-[0.65rem] uppercase tracking-[0.16em] text-mars-orange">
                  {q.id}
                </span>
                <span className="mono text-[0.65rem] uppercase tracking-[0.14em] text-mars-white/40">
                  · {describeQuestion(q)}
                </span>
                {isRandomizedQuestion(q) ? <RandomizedChip /> : null}
              </div>
              <p className="mt-1.5 text-sm text-mars-white">{questionLabel(q)}</p>
            </li>
          ))}
        </ol>
      </section>
    </div>
  );
}
